import { clientQuery, clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import {
  ACTIVITY_DAYS,
  DAY_MS,
  TRAILS_LIMIT,
  TRIPS_LIMIT,
  UNITS_PAGE_SIZE,
  UNITS_TARGET_COUNT,
} from '../utils/constants';
import { toUtcIso } from '../utils/timeZone';
import { isLightMetricsItem } from '../utils/unit';

// `unit.timeZone.name` exists in both GraphQL APIs; `timeZoneName` only in one of them.
const SEARCH_VIDEO_DEVICES = `
  query requestVideoUnits($openSearch: String, $limit: Int, $offset: Int) {
    searchVideoDevices(openSearch: $openSearch, limit: $limit, offset: $offset) {
      total
      items {
        linkedDevice { id manufacturerName deviceModelName }
        unit { id label esn timeZone { name } }
      }
    }
  }`;

const SEARCH_ACTIVITY_DATES = `
  query requestVideoActivityDates($deviceId: String!, $startDate: AWSDateTime!, $endDate: AWSDateTime!, $includeLinkedData: Boolean) {
    searchVehicleActivityDates(deviceId: $deviceId, startDate: $startDate, endDate: $endDate, includeLinkedData: $includeLinkedData) {
      items { date }
    }
  }`;

const SEARCH_TRIPS = `
  query requestVideoTrips($deviceId: String!, $startDate: AWSDateTime!, $endDate: AWSDateTime!, $limit: Int, $offset: Int) {
    searchVehicleTrips(deviceId: $deviceId, startDate: $startDate, endDate: $endDate, limit: $limit, offset: $offset) {
      items {
        start { unitTime driverName }
        end { unitTime }
      }
    }
  }`;

const SEARCH_TRAILS = `
  query requestVideoTrail($deviceId: String!, $startDate: AWSDateTime!, $endDate: AWSDateTime!, $limit: Int, $offset: Int, $includeLinkedData: Boolean) {
    searchVehicleTrails(deviceId: $deviceId, startDate: $startDate, endDate: $endDate, limit: $limit, offset: $offset, includeLinkedData: $includeLinkedData) {
      items { id eventCode eventName speed ignitionStatus unitTime deviceData }
    }
  }`;

const CREATE_CLIP_REQUEST = `
  mutation requestVideoCreateClip($deviceId: String!, $dateRange: AWSDateTimeRange!, $isPrivate: Boolean) {
    createClipRequest(deviceId: $deviceId, dateRange: $dateRange, isPrivate: $isPrivate) {
      id deviceId status startDate endDate
    }
  }`;

const queryMain = (query, variables) => clientQuery(query, variables, endpoints.GRAPHQL_GENERAL);

// The server cannot filter by manufacturer: scan pages and keep LightMetrics cameras
// until enough are found or the list ends. `nextOffset` is undefined when nothing is left.
export const fetchVideoUnits = async ({ search, offset = 0 }) => {
  const items = [];
  let cursor = offset;
  let total = Infinity;

  while (items.length < UNITS_TARGET_COUNT && cursor < total) {
    const data = await queryMain(SEARCH_VIDEO_DEVICES, {
      openSearch: search || undefined,
      limit: UNITS_PAGE_SIZE,
      offset: cursor,
    });
    const page = data?.searchVideoDevices;
    const pageItems = page?.items || [];
    total = page?.total ?? 0;
    cursor += UNITS_PAGE_SIZE;
    items.push(...pageItems.filter(isLightMetricsItem));
    if (pageItems.length === 0) total = 0;
  }

  return { items, nextOffset: cursor < total ? cursor : undefined };
};

export const fetchVideoUnitById = async (cameraDeviceId) => {
  let offset = 0;
  while (offset !== undefined) {
    const page = await fetchVideoUnits({ offset });
    const found = page.items.find((item) => item.linkedDevice.id === cameraDeviceId);
    if (found) return found;
    offset = page.nextOffset;
  }
  return null;
};

export const fetchActivityDates = async ({ activityUnitId }) => {
  const now = Date.now();
  const data = await queryMain(SEARCH_ACTIVITY_DATES, {
    deviceId: activityUnitId,
    includeLinkedData: true,
    startDate: toUtcIso(now - ACTIVITY_DAYS * DAY_MS),
    endDate: toUtcIso(now),
  });
  return (data?.searchVehicleActivityDates?.items || []).map((item) => item.date);
};

export const fetchTrips = async ({ activityUnitId, startMs, endMs }) => {
  const data = await queryMain(SEARCH_TRIPS, {
    deviceId: activityUnitId,
    startDate: toUtcIso(startMs),
    endDate: toUtcIso(endMs),
    limit: TRIPS_LIMIT,
    offset: 0,
  });
  return data?.searchVehicleTrips?.items || [];
};

export const fetchTrail = async ({ activityUnitId, startMs, endMs }) => {
  const data = await queryMain(SEARCH_TRAILS, {
    deviceId: activityUnitId,
    includeLinkedData: true,
    startDate: toUtcIso(startMs),
    endDate: toUtcIso(endMs),
    limit: TRAILS_LIMIT,
    offset: 0,
  });
  return data?.searchVehicleTrails?.items || [];
};

export const createClipRequest = async ({ cameraDeviceId, startMs, endMs, isPrivate }) => {
  const data = await clientMutation(
    CREATE_CLIP_REQUEST,
    {
      deviceId: cameraDeviceId,
      dateRange: { start: toUtcIso(startMs), end: toUtcIso(endMs) },
      isPrivate,
    },
    endpoints.GRAPHQL_GENERAL
  );
  return data?.createClipRequest;
};
